import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, Check } from "lucide-react";
import {
  onInstallAvailability,
  triggerInstall,
  isStandalone,
} from "../pwa";
import LanguageSwitcher from "./LanguageSwitcher";
import ReviewDialog from "./ReviewDialog";

export default function Header() {
  const { t } = useTranslation();
  const [canInstall, setCanInstall] = useState(false);
  const [installed, setInstalled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setInstalled(isStandalone());
    return onInstallAvailability(setCanInstall);
  }, []);

  const handleInstall = async () => {
    const ok = await triggerInstall();
    if (ok) setInstalled(true);
  };

  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-background/70 border-b border-border/60">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Link
            to="/"
            className="flex items-center gap-3 group min-w-0"
            data-testid="header-logo-link"
          >
            <img
              src="/icon-192.png"
              alt="iDiag"
              className="h-9 w-9 rounded-xl ring-1 ring-white/10 shadow-lg shadow-primary/10 transition-transform group-hover:scale-105 shrink-0"
            />
            <div className="leading-tight">
              <div className="font-extrabold tracking-tight text-lg">
                {t("app.name")}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {t("app.tagline")}
              </div>
            </div>
          </Link>
          <ReviewDialog />
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher />
          {!installed && canInstall && (
            <button
              onClick={handleInstall}
              data-testid="header-install-button"
              className="hidden sm:inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:brightness-110 transition"
            >
              <Download className="w-4 h-4" />
              {t("nav.install")}
            </button>
          )}
          {installed && (
            <span
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-secondary text-xs font-medium text-muted-foreground"
              data-testid="header-installed-badge"
            >
              <Check className="w-3.5 h-3.5" />
              {t("nav.installed")}
            </span>
          )}
          {/* Hidden link to /historial accessible via direct URL only — kept hidden in nav */}
          <Link
            to="/historial"
            aria-hidden="true"
            tabIndex={-1}
            className="sr-only"
            data-testid="header-historial-hidden-link"
          >
            {t("nav.history")}
          </Link>
          {location.pathname.startsWith("/historial") && (
            <Link
              to="/"
              data-testid="header-back-home"
              className="text-sm text-muted-foreground hover:text-foreground transition"
            >
              ←
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
